import React from 'react';
import styled, { css } from 'styled-components/macro';
import { Modal } from 'src/client/components/modals/Modal/Modal';
import { formatAmountInWords } from '@tovia/man-app-utils/lib/helpers/currencyToWords';
import PurchaseGalleryForm from './PurchaseGalleryForm';
import PurchaseGalleryUser from './PurchaseGalleryUser';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { BlurrableImage } from 'src/client/components/ui/BlurredImage';
import { MOBILE_LANDSCAPE } from 'src/client/constants/mediaQueries';
import { GetGalleryCoverUrlFn } from 'src/client/helpers/urls/useGetGalleryCoverUrl';
import { CurrencySymbols } from 'src/shared/constants/currency';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';

type PurchaseGalleryDialogProps = {
  className?: string;
  currency: string;
  description?: string;
  onHide: () => void;
  price: number;
  purchaseError?: unknown;
  purchaseLoading?: boolean;
  purchaseRedirect?: string;
  purchaseScene: ({ email, password }: { email: string; password: string }) => void;
  purchaseStatus?: boolean;
  quality?: string;
  runtime?: string;
  show?: boolean;
  submitted: boolean;
  thumbnail: ReturnType<GetGalleryCoverUrlFn>;
  title?: string;
  type: 'gallery' | 'movie';
  user: boolean;
  validating?: boolean;
  vatPct: number;
  wideThumbnail: ReturnType<GetGalleryCoverUrlFn>;
  year: number;
};

export const PurchaseGalleryDialog = (props: PurchaseGalleryDialogProps) => {
  const {
    className,
    currency,
    description = '',
    onHide,
    price,
    purchaseError,
    purchaseLoading,
    purchaseRedirect,
    purchaseScene,
    purchaseStatus,
    quality = '',
    runtime = '',
    show = false,
    submitted,
    title = '',
    type,
    user,
    validating,
    vatPct,
    year,
  } = props;

  const { country } = useSelector((state) => state.auth.location);
  const showSfwSite = useShowSfwSite();

  const currencySymbol = CurrencySymbols[currency];

  return (
    <StyledModal
      show={show}
      onHide={onHide}
      dialogClassName="modal-dialog"
      modalBodyClassName={`${className} modal-body`}
    >
      <div className="modal-wrapper">
        <PortraitThumbnail $isBlurred={props.thumbnail.blurImage} src={props.thumbnail.url} type={type} />
        <LandscapeThumbnail $isBlurred={props.wideThumbnail.blurImage} src={props.wideThumbnail.url} type={type} />

        <div className="info-wrapper">
          <div className="title-wrapper">
            <span className="gallery-title">{`${title} (${year})`}</span>
          </div>
          {!!description && !showSfwSite && (
            <p className="gallery-description">
              {description.split(/\s+/).slice(0, 25).join(' ')}
              ...
            </p>
          )}
          <div className="gallery-stats">
            {!!runtime && <span className="stats-item">Runtime: {runtime}</span>}
            <span className="stats-item">Quality: {quality}</span>
            {vatPct <= 0 && (
              <span className="stats-item">
                One-time purchase: {currencySymbol}
                {currencySymbol === CurrencySymbols.JPY ? price : price.toFixed(2)}
              </span>
            )}
          </div>
          {!user ? (
            <div className="purchase-section">
              <p className="form-title">Get started with your purchase</p>
              <PurchaseGalleryForm onSubmit={purchaseScene} submitted={submitted} />
              <p className="sub-title">You will be redirected to our secure gateway.</p>
              <i className="security-icons" />
            </div>
          ) : (
            <div className="purchase-section">
              <PurchaseGalleryUser
                purchaseError={purchaseError}
                purchaseLoading={purchaseLoading}
                purchaseRedirect={purchaseRedirect}
                purchaseScene={purchaseScene}
                purchaseStatus={purchaseStatus}
                submitted={submitted}
                validating={validating}
              />
            </div>
          )}
          <span className="file-delivery">
            {user && <VatText country={country} currency={currency} price={price} vatPct={vatPct} />}
            File delivery: Plays immediately in your browser and a DRM-free link will be emailed to you.
          </span>
        </div>
      </div>
    </StyledModal>
  );
};

type VatTextParams = {
  country?: string;
  currency?: string;
  price?: number;
  vatPct?: number;
};

const VatText = ({ country, currency, price, vatPct }: VatTextParams) => {
  if (!price || !currency || !vatPct || vatPct <= 0) {
    return <></>;
  }

  const vatAmount = price * vatPct;

  const priceWords = formatAmountInWords(price, currency);
  const vatWords = formatAmountInWords(vatAmount, currency);

  const priceWithVat = price + vatAmount;
  const priceWithVatWords = formatAmountInWords(priceWithVat, currency);

  const taxName = country === 'US' ? 'state tax' : 'VAT';

  return (
    <>
      By proceeding with and finalizing this transaction, you hereby acknowledge, understand, and fully consent to be
      bound by all the conditions, stipulations, and provisions outlined in the terms of service, as they apply to this
      agreement. The total amount due is {priceWords} plus {vatWords} {taxName} for a total of {priceWithVatWords}
      .&nbsp;
    </>
  );
};

const PortraitThumbnail = styled(BlurrableImage)<{ type: string }>`
  max-width: 50%;
  height: 100%;
  aspect-ratio: 229 / 345;
  margin: 0 auto;

  ${(props) =>
    props.type === 'movie' &&
    css`
      display: none;
      @media ${MOBILE_LANDSCAPE} {
        display: block;
      }
    `}
`;

const LandscapeThumbnail = styled(BlurrableImage)<{ type: string }>`
  max-width: 100%;
  display: ${(props) => (props.type === 'movie' ? 'block' : 'none')};
  @media ${MOBILE_LANDSCAPE} {
    display: none;
  }
`;

const StyledModal = styled(Modal)`
  & .modal-wrapper {
    display: flex;
    flex-direction: row;

    @media (max-width: 567px) {
      flex-direction: column;
    }
  }

  & .info-wrapper {
    display: flex;
    flex-direction: column;
    @media (min-width: 567px) {
      margin: 0 16px;
    }
  }

  & .purchase-section {
    order: 2;

    @media (max-width: 567px) {
      order: 1;
    }

    .form-title {
      margin: 8px 0;
    }

    .sub-title {
      font-size: 0.85rem;
      text-align: center;
    }
  }

  & .title-wrapper {
    order: 2;

    .gallery-title {
      font-size: 1.72rem;
    }
  }

  & .gallery-stats {
    order: 2;

    .stats-item {
      display: block;
    }
  }

  & .gallery-description {
    margin: 8px 0;
    order: 2;
  }

  & .file-delivery {
    font-size: 0.75rem;
    margin-top: 12px;
    order: 2;

    @media (min-width: 568px) {
      text-align: justify;
    }
  }
`;
